<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>用户管理</span>
        <div class="header-actions">
          <el-button :loading="exporting" @click="exportData">导出用户</el-button>
          <el-button :loading="loading" @click="loadData">刷新</el-button>
        </div>
      </div>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.nickname" placeholder="昵称" clearable />
        </el-form-item>
        <el-form-item>
          <el-input v-model.trim="query.mobile" placeholder="手机号" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.status" placeholder="状态" clearable>
            <el-option :value="1" label="启用" />
            <el-option :value="0" label="禁用" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="用户数据加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="没有匹配的用户数据" />

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column label="用户" min-width="220">
              <template slot-scope="{ row }">
                <div class="user-cell">
                  <el-avatar :size="42" :src="row.avatar">{{ (row.nickname || '用户').slice(0, 1) }}</el-avatar>
                  <div class="user-cell__meta">
                    <div class="user-cell__name">{{ row.nickname || '未命名用户' }}</div>
                    <div class="sub-text">ID {{ row.id }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="mobile" label="手机号" width="150" />
            <el-table-column label="性别" width="90">
              <template slot-scope="{ row }">
                {{ genderText(row.gender) }}
              </template>
            </el-table-column>
            <el-table-column prop="points" label="积分" width="100" />
            <el-table-column label="状态" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'">
                  {{ Number(row.status) === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="最近登录" min-width="180">
              <template slot-scope="{ row }">
                {{ formatDateTime(row.lastLoginTime) }}
              </template>
            </el-table-column>
            <el-table-column label="创建时间" min-width="180">
              <template slot-scope="{ row }">
                {{ formatDateTime(row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openDetail(row)">查看详情</el-button>
                <el-button
                  type="text"
                  :class="{ 'danger-text': Number(row.status) === 1 }"
                  @click="toggleStatus(row)"
                >
                  {{ Number(row.status) === 1 ? '禁用' : '启用' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>

    <el-dialog
      title="用户详情"
      :visible.sync="detailVisible"
      width="720px"
    >
      <div v-loading="detailLoading">
        <el-empty v-if="!detailLoading && !detail" description="暂无用户详情" />
        <template v-else-if="detail">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="用户ID">{{ detail.id }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{ detail.nickname || '-' }}</el-descriptions-item>
            <el-descriptions-item label="手机号">{{ detail.mobile || '-' }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag :type="Number(detail.status) === 1 ? 'success' : 'info'">
                {{ Number(detail.status) === 1 ? '启用' : '禁用' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="性别">{{ genderText(detail.gender) }}</el-descriptions-item>
            <el-descriptions-item label="积分">{{ detail.points || 0 }}</el-descriptions-item>
            <el-descriptions-item label="最近登录">{{ formatDateTime(detail.lastLoginTime) }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
            <el-descriptions-item label="OpenID" :span="2">{{ detail.openid || '-' }}</el-descriptions-item>
            <el-descriptions-item label="UnionID" :span="2">{{ detail.unionid || '-' }}</el-descriptions-item>
          </el-descriptions>

          <div class="detail-stats">
            <div class="detail-stats__item">
              <div class="detail-stats__value">{{ detail.orderCount || 0 }}</div>
              <div class="detail-stats__label">普通订单</div>
            </div>
            <div class="detail-stats__item">
              <div class="detail-stats__value">{{ detail.pointOrderCount || 0 }}</div>
              <div class="detail-stats__label">积分订单</div>
            </div>
            <div class="detail-stats__item">
              <div class="detail-stats__value">{{ detail.favoriteCount || 0 }}</div>
              <div class="detail-stats__label">收藏数</div>
            </div>
            <div class="detail-stats__item">
              <div class="detail-stats__value">{{ detail.footprintCount || 0 }}</div>
              <div class="detail-stats__label">足迹数</div>
            </div>
          </div>

          <el-descriptions :column="3" border class="detail-extra">
            <el-descriptions-item label="待付款">{{ detail.pendingPayCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="待发货">{{ detail.pendingDeliveryCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="待收货">{{ detail.pendingReceiveCount || 0 }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </div>
      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { exportUserPage, getUserDetail, getUserPage, updateUserStatus } from '../../api/admin'
import { formatDateTime } from '../../utils/format'

const createQuery = () => ({
  nickname: '',
  mobile: '',
  status: '',
  pageNum: 1,
  pageSize: 10
})

export default {
  data() {
    return {
      query: createQuery(),
      list: [],
      total: 0,
      loading: false,
      exporting: false,
      loadError: false,
      detailVisible: false,
      detailLoading: false,
      detail: null
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatDateTime,
    genderText(gender) {
      if (Number(gender) === 1) return '男'
      if (Number(gender) === 2) return '女'
      return '未知'
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const result = await getUserPage(this.query)
        this.list = (result && result.list) || []
        this.total = Number(result && result.total) || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = createQuery()
      this.loadData()
    },
    changePage(pageNum) {
      this.query.pageNum = pageNum
      this.loadData()
    },
    changePageSize(pageSize) {
      this.query.pageSize = pageSize
      this.query.pageNum = 1
      this.loadData()
    },
    async exportData() {
      this.exporting = true
      try {
        const params = {
          nickname: this.query.nickname,
          mobile: this.query.mobile,
          status: this.query.status
        }
        await exportUserPage(params)
        this.$message.success('用户导出已开始下载')
      } catch (error) {
        this.$message.error((error && error.message) || '用户导出失败，请稍后重试')
      } finally {
        this.exporting = false
      }
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        this.detail = await getUserDetail(row.id)
      } catch (error) {
        this.$message.error((error && error.message) || '用户详情加载失败')
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    toggleStatus(row) {
      const nextStatus = Number(row.status) === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '禁用'
      this.$confirm(`确认${actionText}用户“${row.nickname || row.id}”吗？`, `${actionText}用户`, {
        type: nextStatus === 1 ? 'info' : 'warning'
      }).then(async () => {
        try {
          await updateUserStatus(row.id, nextStatus)
          this.$message.success(`${actionText}成功`)
          this.loadData()
          if (this.detail && this.detail.id === row.id) {
            this.detail.status = nextStatus
          }
        } catch (error) {
          this.$message.error((error && error.message) || `${actionText}失败，请稍后重试`)
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.query-form {
  margin-bottom: 18px;
}

.state-block {
  margin-bottom: 16px;
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-cell__meta {
  min-width: 0;
}

.user-cell__name {
  color: #303133;
  font-weight: 600;
}

.sub-text {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
}

.table-footer__meta {
  color: #606266;
  font-size: 13px;
}

.detail-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.detail-stats__item {
  padding: 16px 12px;
  text-align: center;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  background: #fafafa;
}

.detail-stats__value {
  color: #303133;
  font-size: 22px;
  font-weight: 700;
}

.detail-stats__label {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}

.detail-extra {
  margin-top: 18px;
}

.danger-text {
  color: #f56c6c;
}
</style>
